import * as ImagePicker from 'expo-image-picker';
import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { ActionSheetContent } from './action-sheet';
import { CombinedActionSheet } from './combined-action-sheet';
import { usePermissionDeniedAlert } from '@/hooks/use-permission-denied-alert';

interface Action {
  action: () => Promise<ImagePicker.ImagePickerResult>;
  checkPermission: () => Promise<ImagePicker.PermissionResponse>;
  name: string;
  requestPermission: () => Promise<ImagePicker.PermissionResponse>;
  trackingName: string;
}

// Wrap actions in functions so unit tests can mock them
const ACTIONS: Action[] = [
  {
    action: async () => await ImagePicker.launchCameraAsync(),
    checkPermission: async () => ImagePicker.getCameraPermissionsAsync(),
    name: i18n.t('take_a_photo'),
    requestPermission: async () => ImagePicker.requestCameraPermissionsAsync(),
    trackingName: 'take_a_photo_button',
  },
  {
    action: async () => await ImagePicker.launchImageLibraryAsync(),
    checkPermission: async () => ImagePicker.getMediaLibraryPermissionsAsync(),
    name: i18n.t('select_from_library'),
    requestPermission: async () =>
      ImagePicker.requestMediaLibraryPermissionsAsync(),
    trackingName: 'select_from_library_button',
  },
];

enum Route {
  Base = 'BASE',
  RequestPermission = 'REQUEST_PERMISSION',
}

interface Props {
  isVisible: boolean;
  onClose: () => void;
  onSelect: (result: ImagePicker.ImagePickerResult) => void;
  title?: string;
}

export const ImagePickerActionSheet: React.FC<Props> = ({
  isVisible,
  onClose,
  onSelect,
  title,
}) => {
  const showPermissionDeniedAlert = usePermissionDeniedAlert();

  const [selectedAction, setSelectedAction] = useState<Action | null>(null);

  if (!isVisible) {
    return null;
  }

  return (
    <CombinedActionSheet
      initialRoute={Route.Base}
      onClose={onClose}
      renderScene={({ routeKey, jumpTo }) => {
        switch (routeKey) {
          case Route.Base:
            return (
              <ActionSheetContent
                actions={ACTIONS.map((action) => action.name)}
                onAction={async (_, index) => {
                  const { action, checkPermission, trackingName } =
                    ACTIONS[index];

                  Analytics.track(
                    `image_picker_action_sheet.${trackingName}.press`,
                  );

                  const result = await checkPermission();
                  switch (result.status) {
                    case ImagePicker.PermissionStatus.GRANTED:
                      onSelect(await action());
                      break;
                    case ImagePicker.PermissionStatus.DENIED:
                      showPermissionDeniedAlert(
                        i18n.t('photos_permission_denied_message'),
                      );
                      break;
                    default:
                      setSelectedAction(ACTIONS[index]);
                      jumpTo(Route.RequestPermission);
                      break;
                  }
                }}
                title={title}
              />
            );
          case Route.RequestPermission:
            return selectedAction ? (
              <ActionSheetContent
                actions={[i18n.t('continue')]}
                message={i18n.t('photos_permission_request_message')}
                onAction={async () => {
                  Analytics.track(
                    `image_picker_action_sheet.${selectedAction.trackingName}_continue_button.press`,
                  );

                  const result = await selectedAction.requestPermission();
                  switch (result.status) {
                    case ImagePicker.PermissionStatus.GRANTED:
                      onSelect(await selectedAction.action());
                      break;
                    case ImagePicker.PermissionStatus.DENIED:
                      showPermissionDeniedAlert(
                        i18n.t('photos_permission_denied_message'),
                      );
                      break;
                  }

                  onClose();
                }}
                title={selectedAction.name}
              />
            ) : null;
        }
      }}
      routes={{
        [Route.Base]: {
          previousKey: null,
          // Disable close button to adhere to App Store guidelines
          showCloseButton: false,
        },
        [Route.RequestPermission]: {
          previousKey: null,
          // Disable close button to adhere to App Store guidelines
          showCloseButton: false,
        },
      }}
    />
  );
};
